import { type Blocker, type BlockerFunction, useBlocker, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { GetWSMgr, ReRenderCanvas, ResizeApp } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { isNullish } from "@theorgchartdev/storybook";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumUIModes } from "@coreapp/constants";
import { appstrp } from "@coreapp/utils/appstrp";
import { appstr } from "@coreapp/utils/appstr";
import { getBoxLayoutExitAlert } from "@coreapp/utils/getBoxLayoutExitAlert";
import { isBoxLayoutOverlayExit, getOverlayExitParams } from "./boxLayoutExitNav";

export type BlockPayload = {
  blocker: Extract<Blocker, { state: "blocked" }>;
  state: Nullable<Parameters<BlockerFunction>[0]>;
};

const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation, historyAction }) => {
  const wsMgr = GetWSMgr();
  if (isNullish(wsMgr)) {
    return false;
  }

  // Pass each location's search params explicitly — window.location.search
  // still reflects the pre-navigation state, so both would read the same value.
  const currentUIMode = Globals._uiConfig?.GetUIMode(
    `/app${currentLocation.pathname}`,
    currentLocation.search
  );
  const nextUIMode = Globals._uiConfig?.GetUIMode(
    `/app${nextLocation.pathname}`,
    nextLocation.search
  );

  // Reports and settings pages implement their OWN useBlocker hooks for component-level
  // dirty state (e.g., unsaved report edits, unsaved settings changes). Those are separate
  // from app-level dirty state (doc dirty, background dirty) that canExitMode checks.
  // Skip them here to avoid double-blocking — React Router only shows one blocker at a time.
  switch (currentUIMode) {
    case enumUIModes.reports:
    case enumUIModes.settings:
      return false;
  }

  // Box layout editor owns its exit gate. Opening directory/chart-explorer is a
  // same-path search-param add that canExitMode treats as in-mode URL cleanup and
  // waves through — but it leaves the BLE. Intercept it so handleBlocked can
  // redirect to the chart route (clean exit) or prompt first (dirty). REPLACE navs
  // are programmatic (security param-strip, legacy redirect), never a user exit.
  if (
    currentUIMode === enumUIModes.boxlayouteditor &&
    nextUIMode === enumUIModes.boxlayouteditor &&
    wsMgr.fLayoutPanel != null &&
    historyAction !== "REPLACE" &&
    isBoxLayoutOverlayExit(currentLocation.search, nextLocation.search)
  ) {
    return true;
  }

  // Unified dirty check via canExitMode
  return !wsMgr.canExitMode(currentUIMode, nextUIMode);
};

const handleBlocked = (payload: BlockPayload) => {
  const { blocker, state } = payload;
  const wsMgr = GetWSMgr();
  if (isNullish(state) || isNullish(wsMgr)) {
    blocker.proceed();
    return;
  }

  const { currentLocation, nextLocation } = state;
  const currentUIMode = Globals._uiConfig?.GetUIMode(
    `/app${currentLocation.pathname}`,
    currentLocation.search
  );
  const nextUIMode = Globals._uiConfig?.GetUIMode(
    `/app${nextLocation.pathname}`,
    nextLocation.search
  );

  // Determine which type of dirty state is blocking
  const dirtyType = wsMgr.getDirtyBlockType(currentUIMode, nextUIMode);

  const onProceed = () => {
    blocker.proceed();
    setTimeout(() => {
      ReRenderCanvas();
      ResizeApp();
    }, 500);
  };

  let alertParams: Nullable<{
    header: string;
    message: string;
    confirmStr: string;
    onConfirm: () => Promise<void | { success: boolean }> | void;
    denyStr: string;
    onDeny: () => Promise<void | false> | void | false;
    deferProceed?: boolean;
  }> = null;

  switch (dirtyType) {
    case "docDirty": {
      const docMgr = wsMgr.GetDocMgr();
      let curFileName = docMgr.GetFileName(false);
      if (curFileName === "") {
        curFileName = appstr("Untitled");
      }

      alertParams = {
        header: `${appstr("Save changes to")} '${curFileName}'?`,
        message: appstr("saveconfirm3"),
        confirmStr: appstr("Save"),
        onConfirm: () => {
          wsMgr.fPostSaveCallback = () => {
            blocker.proceed();
            setTimeout(() => {
              ReRenderCanvas();
              ResizeApp();
            }, 500);
          };
          wsMgr.HandleChangeDoc("ChangeDoc_Save");
        },
        deferProceed: true,
        denyStr: appstr("Discard"),
        onDeny: () => {
          wsMgr.HandleChangeDoc("ChangeDoc_NoSave");
          wsMgr.CloseDocument(false);
          blocker.reset();
          (wsMgr as any).routerNavigate(nextLocation.pathname, { replace: true });
          return false;
        }
      };
      break;
    }
    case "layoutPending": {
      const layoutPanel = wsMgr?.fLayoutPanel;
      if (!layoutPanel) {
        blocker.proceed();
        return;
      }

      layoutPanel.ClearSelection();

      const editBoxStyleMode = layoutPanel.EditBoxStyle?.() ?? false;
      const affectedCount = wsMgr.GetPendingEditCount?.() ?? 0;
      const hasPending = wsMgr.fBoxStylesMgr?.HasAnyPendingChanges?.() ?? false;

      // Opening directory/chart-explorer from the BLE: land on the chart route
      // with the surface open, not overlaid on a torn-down editor.
      const overlayExit =
        currentUIMode === enumUIModes.boxlayouteditor &&
        nextUIMode === enumUIModes.boxlayouteditor &&
        isBoxLayoutOverlayExit(currentLocation.search, nextLocation.search);

      // An overlay exit can't let the router proceed to nextLocation (the torn-down BLE
      // URL), so it redirects to the chart route carrying the surface params instead.
      const leave = overlayExit
        ? () => {
            blocker.reset();
            (wsMgr as any).exitBoxLayout?.(
              getOverlayExitParams(currentLocation.search, nextLocation.search)
            );
          }
        : onProceed;

      if (overlayExit && !hasPending) {
        // Nothing to save — leave silently and open the surface on the chart.
        wsMgr.DoCancelAndExit(null, false);
        leave();
        return;
      }

      const cfg = getBoxLayoutExitAlert(editBoxStyleMode, affectedCount);
      alertParams = {
        header: cfg.header,
        message: cfg.message,
        confirmStr: cfg.saveConfirmStr,
        deferProceed: true,
        // DoCommitAndExit returns {success:false} on save failure; skip leave() so the blocker stays blocked.
        onConfirm: async () => {
          const result = await wsMgr.DoCommitAndExit(null, false);
          if (result?.success === false) return { success: false };
          leave();
        },
        denyStr: cfg.saveDenyStr,
        onDeny: () => {
          wsMgr.DoCancelAndExit(null, false);
          leave();
          return false;
        }
      };
      break;
    }
    case "backgroundDirty": {
      alertParams = {
        header: appstrp("Save background", "?"),
        message: appstr("Save background alert"),
        confirmStr: appstr("Save background"),

        onConfirm: () => {
          if (wsMgr.IsBackgroundDirty()) {
            wsMgr.fSaveCancelledCallback = () => blocker.reset();
            wsMgr.DoSaveBackground(true, () => onProceed());
          } else {
            onProceed();
          }
        },
        deferProceed: true,
        denyStr: appstr("Discard Changes"),
        onDeny: () => {
          wsMgr.discardBackgroundEditorChanges();
        }
      };
      break;
    }
    case "viewDirty": {
      const doc = wsMgr.GetDoc();
      const activeView = doc?.GetActiveTemplate();
      const viewName = activeView?.GetTemplateName() ?? "";

      alertParams = {
        header: `Save your changes to view ${viewName}?`,
        message: appstr("saveconf"),
        confirmStr: appstr("Save Changes"),
        onConfirm: () => {
          wsMgr.DoSaveActiveView();
        },
        denyStr: appstr("Discard Changes"),
        onDeny: () => {
          wsMgr.discardActiveViewChanges();
        }
      };
      break;
    }
  }

  if (isNullish(alertParams)) {
    blocker.proceed();
    return;
  }

  const { onConfirm, onDeny } = alertParams;

  wsMgr.CreateReactAlert(
    alertParams.header,
    alertParams.message,
    async () => {
      const result = await onConfirm();
      // DoCommitAndExit returns {success:false} on save failure; blocker stays blocked so the user can retry.
      if (result?.success === false) return;
      if (!alertParams.deferProceed) onProceed();
    },
    async () => {
      const result = await onDeny();
      if (result !== false) onProceed();
    },
    alertParams.confirmStr,
    alertParams.denyStr,
    blocker.reset
  );
};

export const useDirtyBlocker = () => {
  const blockerState = useRef<BlockPayload["state"]>(null);
  const prevModeRef = useRef<number | null>(null);
  const [loginComplete, setLoginComplete] = useState(() => !!GetWSMgr()?.fLoadComplete);
  useTopic("LoginComplete", () => setLoginComplete(true));
  // BLE entry path (DoBoxLayoutEditorMode → CloneLayout →
  // doc.FinishLoadLayout) reconciles cell.fFieldID against the chart's
  // live boxClass via GetFieldDefByID. If the chart RFC hasn't
  // completed when FinishLoadLayout runs (cold-start /boxlayout/ URL
  // path), GetFieldDefByID returns null — and QLayoutMgr.js:505
  // PERMANENTLY zeroes out the cell's saved fFieldID/fFieldName/
  // fFieldClassName metadata in that case. Later chart-data arrival
  // can't re-resolve because the saved IDs are gone. Gate
  // enterMode(boxlayouteditor) on chartDocumentLoaded so
  // FinishLoadLayout always runs against a hydrated ObjDB.
  const [chartDataLoaded, setChartDataLoaded] = useState(false);
  useTopic("chartDocumentLoaded", () => setChartDataLoaded(true));

  const blocker = useBlocker(args => {
    blockerState.current = args;
    return shouldBlock(args);
  });

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    const payload: BlockPayload = { blocker, state: blockerState.current };

    handleBlocked(payload);
  }, [blocker.state]);

  const location = useLocation();
  useEffect(() => {
    const wsMgr = GetWSMgr();
    if (isNullish(wsMgr)) return;

    // Don't fire until coreapp startup is complete.
    // The startup flow calls enterMode synchronously during init.
    // This effect only handles subsequent navigations after the app is ready.
    if (!loginComplete) {
      return;
    }

    const locationUIMode = Globals._uiConfig?.GetUIMode(
      `/app${location.pathname}`,
      location.search
    );

    // Narrow race-condition gate: enterMode(boxlayouteditor) triggers
    // DoBoxLayoutEditorMode → CloneLayout → doc.FinishLoadLayout. The
    // FinishLoadLayout at QLayoutMgr.js:505 *permanently clears* a
    // cell's saved fFieldID/fFieldName/fFieldClassName if the field
    // def resolution (boxClass.GetFieldDefByID) returns null — and on
    // cold-start /boxlayout/ URL, this effect fires before the chart
    // RFC completes, so ObjDB is empty and every saved field ref
    // gets nuked. Waiting for chartDocumentLoaded before entering
    // box layout ensures FinishLoadLayout always runs against a
    // hydrated ObjDB. No other mode hits this destructive path, so
    // the gate is scoped only to boxlayouteditor.
    if (locationUIMode === enumUIModes.boxlayouteditor && !chartDataLoaded) {
      return;
    }

    if (locationUIMode !== prevModeRef.current) {
      if (prevModeRef.current != null) {
        wsMgr.exitMode(prevModeRef.current, locationUIMode);
        wsMgr.enterMode(locationUIMode, prevModeRef.current);
        prevModeRef.current = locationUIMode;
      } else {
        // First render after login. Startup entered chart mode.
        prevModeRef.current = enumUIModes.chart;
        // If URL wants a different mode (deep link), transition now
        if (locationUIMode !== enumUIModes.chart) {
          wsMgr.enterMode(locationUIMode, enumUIModes.chart);
          prevModeRef.current = locationUIMode;
        }
      }

      // Recover canvas position after mode transitions (e.g., RSB open/close,
      // background mode toggle). Only fires on actual mode changes, not on
      // query-param-only changes (?tool=, ?view=, ?topOfChart=).
      setTimeout(() => {
        ReRenderCanvas();
        ResizeApp();
      }, 500);
    }
  }, [location, loginComplete, chartDataLoaded]);
};
